import { Icon } from "@iconify/react"
import { useQuery, keepPreviousData, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Card, Drawer, Flex, Form, Grid, Input, message, Pagination, Radio, Table, Tag } from "antd"
import TableAction from "../../../components/TableAction";
import { useEffect, useState } from "react";
import useTitleStore from "../../../store/titleStore";
import useDebounce from "../../../hooks/useDebounce";
import { useTableHeight } from "../../../hooks/useTableHeight";
import { useNavigate } from "react-router";
import { masterDataTypeService } from "../../../services/master-data-type.service";

export default function MasterDataTypeIndex(){

  const breakpoint = Grid.useBreakpoint()

  const navigate = useNavigate();

  const [form] = Form.useForm()

  const { setTitle } = useTitleStore();
  const queryClient = useQueryClient();

  const tableHeight = useTableHeight();

  const [messageApi, contextHolder] = message.useMessage();
  const [keyword, setKeyword] = useState("");
  const debouncedKeyword = useDebounce(keyword, 500);
  const [drawerOpt, setDrawerOpt] = useState({
    open: false,
    id: null
  })
  const [dataParams, setDataParams] = useState({
    limit: 10,
    page: 1,
    filter: [
      {
        field: 'name',
        operator: 'like',
        value: debouncedKeyword
      },
    ]
  })

  useEffect(() => {
    setTitle("Kategorisasi");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDataParams((prev) => ({
      ...prev,
      filter: [
        {
          field: 'name',
          operator: 'like',
          value: debouncedKeyword
        }
      ]
    }));
  }, [debouncedKeyword]);

  const { data: masterDataType , isLoading, isRefetching } = useQuery({
    queryKey: ['masterDataType', dataParams],
    queryFn: () => masterDataTypeService.fetchAll(dataParams),
    placeholderData: keepPreviousData,
  })

  const storeMutation = useMutation({
    mutationFn: val => masterDataTypeService.store(val),
    onSuccess: () => {
      queryClient.invalidateQueries('masterDataType');
      messageApi.success('Data berhasil ditambah');
      handleCloseDrawerOpt()
    },
    onError: (error) => {
      console.log(error)
      messageApi.error('Terjadi kesalahan');
    }
  })

  const updateMutation = useMutation({
    mutationFn: val => masterDataTypeService.update(val),
    onSuccess: () => {
      queryClient.invalidateQueries('masterDataType');
      messageApi.success('Data berhasil diubah');
      handleCloseDrawerOpt()
    },
    onError: (error) => {
      console.log(error)
      messageApi.error('Terjadi kesalahan');
    }
  })

  const { mutate: handleDelete, isPending: deleteLoading } = useMutation({
    mutationFn: (id) => masterDataTypeService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries('users');
      messageApi.success('Data berhasil dihapus');
    },
    onError: (error) => {
      console.log(error)
    }
  })

  const handleChangePage = (page, pageSize) => {
    setDataParams({
      ...dataParams,
      limit: pageSize,
      page
    })
  }

  const handleEdit = record => {
    return navigate('/data-selection/data-selection-type/detail/'+record.id) 
    // form.setFieldsValue(record)
    // setDrawerOpt({ open: true, id: record.id })
  }

  const handleCloseDrawerOpt = () => {
    form.resetFields()
    setDrawerOpt({ open: false, id: null })
  }

  const handleSubmit = val => {
    if(drawerOpt.id) updateMutation.mutate({id: drawerOpt.id, ...val})
    else storeMutation.mutate(val)
  }

  const masterDataTypes = masterDataType?.data;
  const pagination = masterDataType?.pagination || { total: 0, page: 1 };

  const columns = [
    {
      title: 'No',
      align: 'center',
      width: 80,
      fixed: breakpoint.md && 'left',
      render: (text, record, index) =>  index + 1,
    },
    {
      title: 'Jenis',
      dataIndex: 'name',
      width: 180
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: val => <Tag color={val ? 'green' : 'red'} >{val ? 'Active' : 'Inactive'}</Tag>
    },
    {
      className: 'last-cell-p',
      title: 'Aksi',
      align: 'right',
      width: 120,
      fixed: 'right',
      render: record => (
        <TableAction
          onClickEdit={() => handleEdit(record)}
          handleDelete={() => handleDelete(record.id)}
          deleteLoading={deleteLoading}
        />
      )
    },
  ]

  return (
    <>
      {contextHolder}
      <Card
        styles={{
          body: {
            paddingLeft: 0,
            paddingRight: 0
          }
        }}
      >
        <Flex gap={20} justify="space-between" style={{ paddingInline: 24, marginBottom: 24 }} >
          <Input 
            style={{ width: 320 }} 
            prefix={<Icon icon="hugeicons:search-01" width="16" height="16" />}
            placeholder="cari"
            onChange={e => setKeyword(e.target.value)}
            value={keyword}
          />
          <Button 
            variant="solid" 
            color="primary"  
            icon={<Icon icon="duo-icons:add-circle" width="22" height="22" />}
            onClick={() => setDrawerOpt({ open: true, id: null })}
            style={{ minWidth: 38 }}
          >
            Tambah Jenis
          </Button>
        </Flex>
        <Table
          columns={columns}
          size="small"
          loading={isLoading || isRefetching}
          rowKey={'id'}
          scroll={{ y: tableHeight }}
          dataSource={masterDataTypes}
          pagination={false}
        />
        <Pagination
          style={{ marginInline: 24, marginTop: 16 }}
          total={pagination.total}
          showSizeChanger
          pageSizeOptions={['10', '20', '50']}
          current={pagination.page}
          pageSize={dataParams.limit}
          align="end"
          onChange={handleChangePage}
        />
      </Card>
      <Drawer
        open={drawerOpt.open}
        title={drawerOpt.id ? "Ubah Data" : "Tambah Data"}
        onClose={handleCloseDrawerOpt}
        footer={
          <Flex gap={16} >
            <Button 
              block
              disabled={storeMutation.isPending} 
              loading={storeMutation.isPending} 
              variant="outlined" 
              color="primary" 
            >
              Batal
            </Button>
            <Button 
              block
              variant="solid" 
              color="primary" 
              onClick={() => form.submit()}
              disabled={storeMutation.isPending} 
              loading={storeMutation.isPending} 
            >
              Simpan
            </Button>
          </Flex>
        }
      > 
        <Form
          layout="vertical"
          onFinish={handleSubmit}
          form={form}
        >
          <Form.Item  rules={[{ required: true }]} name={'name'} label="Jenis" >
            <Input/>
          </Form.Item>
          <Form.Item  name={'description'} label="Deskripsi" >
            <Input.TextArea/>
          </Form.Item>
          <Form.Item  rules={[{ required: true }]} name={'status'} label="Status" >
            <Radio.Group>
              <Radio style={{ marginRight: 80}} value={true} >Active</Radio>
              <Radio value={false} >Inactive</Radio>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Drawer>
    </>
  )
}